import { FC, useMemo, useState } from "react"
import {
  GridColDef,
  GridActionsCellItem,
  GridRowParams,
} from "@mui/x-data-grid-pro"
import VisibilityIcon from "@mui/icons-material/Visibility"
import { DataGrid } from "@/core/components"
import { useAppDispatch, useAppSelector } from "@/modules/app/hooks"
import { formatDateAndTime } from "@/core/utils"
import { GRID_PAGE_SIZE_OPTIONS } from "@/core/constants"
import { OSINT } from "../../interfaces"
import { QUERY_TYPE_BY_KEY } from "../../constants"
import { useListQuery, getSherlokState, setPageSize } from "../../store"
import {
  extractPhones,
  extractReportUrl,
  extractInputDisplay,
} from "../../hooks"
import { StatusChip } from "../cells/statusChip"
import { PhonesCell } from "../cells/phonesCell"
import { ReportLinkCell } from "../cells/reportLinkCell"
import { AwaitingInputModal } from "../awaitingInputModal"

const POLL_INTERVAL_MS = 5000

export const SherlokQueriesTable: FC = () => {
  const dispatch = useAppDispatch()
  const { filters, pageSize, cursorStack, pageIndex, autoRefresh } =
    useAppSelector(getSherlokState)
  const cursor = cursorStack[pageIndex] ?? undefined

  const { data, isFetching, refetch } = useListQuery(
    {
      queryParams: {
        ...filters,
        cursor,
        limit: pageSize,
      },
    },
    { pollingInterval: autoRefresh ? POLL_INTERVAL_MS : 0 },
  )

  const items = data?.items ?? []
  const total = data?.total ?? 0

  // Modal state
  const [modalQuery, setModalQuery] = useState<OSINT.QueryDto | null>(null)

  const columns = useMemo<GridColDef<OSINT.QueryDto>[]>(
    () => [
      {
        headerName: "Создан",
        field: "createdAt",
        width: 160,
        valueFormatter: ({ value }) => (value ? formatDateAndTime(value) : ""),
      },
      {
        headerName: "Тип",
        field: "type",
        width: 140,
        valueGetter: ({ row }) =>
          QUERY_TYPE_BY_KEY[row.payload.type]?.label ?? row.payload.type,
      },
      {
        headerName: "Вход",
        field: "input",
        flex: 1,
        minWidth: 200,
        valueGetter: ({ row }) => extractInputDisplay(row.payload),
      },
      {
        headerName: "Статус",
        field: "status",
        width: 160,
        valueGetter: ({ row }) => row.status,
        renderCell: ({ row }) => (
          <StatusChip status={row.status} error={row.error} />
        ),
      },
      {
        headerName: "Телефоны",
        field: "phones",
        flex: 1.5,
        minWidth: 200,
        valueGetter: ({ row }) => extractPhones(row).join(", "),
        renderCell: ({ row }) => <PhonesCell phones={extractPhones(row)} />,
      },
      {
        headerName: "Отчёт",
        field: "report",
        width: 140,
        valueGetter: ({ row }) => extractReportUrl(row) ?? "",
        renderCell: ({ row }) => <ReportLinkCell url={extractReportUrl(row)} />,
      },
    ],
    [],
  )

  const rowActions = useMemo(
    () => [
      (params: GridRowParams<OSINT.QueryDto>) => {
        if (params.row.status !== "AWAITING_INPUT") {
          return <></>
        }
        return (
          <GridActionsCellItem
            key="press"
            icon={<VisibilityIcon />}
            label="Действия"
            onClick={() => setModalQuery(params.row)}
          />
        )
      },
    ],
    [],
  )

  return (
    <>
      <DataGrid<OSINT.QueryDto>
        columns={columns}
        rows={items}
        getRowId={(row) => row.id}
        loading={isFetching}
        pagination
        paginationMode="server"
        rowCount={total}
        pageSizeOptions={GRID_PAGE_SIZE_OPTIONS}
        paginationModel={{ page: 0, pageSize }}
        onPaginationModelChange={(m) => {
          if (m.pageSize !== pageSize) dispatch(setPageSize(m.pageSize))
        }}
        rowActions={rowActions}
        sx={{
          "& .MuiDataGrid-cell": { whiteSpace: "normal", lineHeight: 1.4 },
        }}
        autoHeight
      />

      <AwaitingInputModal
        open={!!modalQuery}
        query={modalQuery}
        onClose={() => {
          setModalQuery(null)
          refetch()
        }}
      />
    </>
  )
}
